import * as React from "react";
import { useEffect, useState } from "react";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { getAllPoints } from "../../API/points";
import { Avatar, Chip, ChipProps } from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddnewCard from "../../components/addNewCard/addNewCard";
import { ERROR_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import MainTable from "../../components/Datagrid/dataGridTable";
import { deleteCard } from "../../API/points";
import { useSelector } from "react-redux";

type Data = {
  name: string;
  description: string;
  logo: string;
  value: number;
  __v: number;
  _id: string;
};

const initialUpdatingRow: Data = {
  description: "",
  name: "",
  logo: "",
  value: 0,
  __v: 0,
  _id: "",
};

export default function PointsList({ search }: { search: string }) {
  const [loading, setLoading] = useState<boolean>(false);
  const [updateCourse, setUpdateCourse] = useState(false);
  const [updatingRow, setUpdatingRow] = useState<Data>(initialUpdatingRow);
  const [rows, setRows] = useState<Data[]>([]);
  const [searchRows, setSearchRows] = useState<Data[]>([]);
  const user = useSelector((state: any) => state.auth);

  useEffect(() => {
    getFirstData();
  }, []);

  useEffect(() => {
    setSearchRows(rows);
  }, [rows]);

  useEffect(() => {
    if (!search?.trim()) {
      setSearchRows(rows);
    } else {
      const filteredRows = rows.filter(
        (row) =>
          row.name.toLowerCase().includes(search.toLowerCase()) ||
          row.description.toLowerCase().includes(search.toLowerCase())
      );
      setSearchRows(filteredRows);
    }
  }, [search, rows]);

  const handleDelete = async (id) => {
    try {
      const result = await deleteCard(id);
      if (result?.response?.statusCode === 200) {
        SUCCESS_TOAST("Card deleted successfully!");
        getFirstData();
      } else {
        SUCCESS_TOAST(result?.response?.message || "Failed to delete card");
      }
    } catch (error) {
      console.error("Error deleting card:", error);
      SUCCESS_TOAST("Something went wrong while deleting");
    }
  };

  const getFirstData = async () => {
    try {
      setLoading(true);
      const data = await getAllPoints();
      if (!data) {
        setRows([]);
        setSearchRows([]);
        ERROR_TOAST("Error connecting to server!");
      } else {
        setRows(data);
        setSearchRows(data);
      }
    } catch (error) {
      ERROR_TOAST("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  // Base columns visible to all users (including Edit)
  const baseColumns = [
    {
      field: "_id",
      hide: true,
    },
    {
      field: "logo",
      headerName: "Card Logo",
      renderCell: (params: any) => <Avatar src={params.value} />,
      width: 200,
    },
    {
      field: "name",
      headerName: "Card Name",
      width: 200,
    },
    {
      field: "description",
      headerName: "Card Description",
      width: 450,
    },
    {
      field: "value",
      headerName: "Points",
      renderCell: (params: any) => <Chip variant="outlined" size="small" {...getChipProps(params)} />,
      width: 140,
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 50,
      renderCell: (cellValues: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <EditRoundedIcon
            data-testid="EditRoundedIcon"
            style={{ color: "#8E8EA1", cursor: "pointer" }}
            onClick={() => handleCellClick(cellValues)}
          />
        </div>
      ),
    },
  ];

  // Delete column visible only to superusers
  const deleteColumn = [
    {
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (cellValues: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <DeleteRoundedIcon
            data-testid="DeleteRoundedIcon"
            style={{ color: "#8E8EA1", cursor: "pointer" }}
            onClick={() => handleDelete(cellValues.row._id)}
          />
        </div>
      ),
    },
  ];

  // Combine columns based on user.isSuper
  const columns = user.isSuper || user.isContentAdmin ? [...baseColumns, ...deleteColumn] : baseColumns;

  const handleCellClick = (value: GridRenderCellParams) => {
    setUpdateCourse(true);
    setUpdatingRow(value.row);
  };

  function getChipProps(params: GridRenderCellParams): ChipProps {
    return {
      label: params.value,
      style: {
        background: "#FA8F1C",
        width: "63px",
        height: "30px",
        color: "#fff",
        fontFamily: "Poppins",
        fontWeight: 500,
        fontSize: "11px",
        borderColor: "#FA8F1C",
      },
    };
  }

  const handelClose = () => {
    setUpdateCourse(false);
    getFirstData();
  };

  return (
    <>
      <MainTable rows={searchRows} columns={columns} loading={loading} />
      {updateCourse && <AddnewCard handelClose={handelClose} data={updatingRow} />}
    </>
  );
}